// react
import { useRef, useState, KeyboardEvent } from 'react'

// external libs
import { Box, Button, IconButton, InputBase, makeStyles, Paper } from '@material-ui/core'
import { Send, AttachFile, HighlightOff } from '@material-ui/icons'

// hooks
import useTempFileService from '../../../../services/web/useTempFileService'
import useAlertMessageAction from '../../../../stores/appAlertMessage/appAlertMessageAction'
import useTranslate from '../../../../i18n/useTranslate'
import useMessenger from '../../../../services/web/useMessenger'
import useCheckPermission from '../../../../utilities/checkPermission'

// utils
import { MAX_FILE_SIZE, accept } from './messenger.helper'
import { getFileIconPath } from '../../../../utilities/pathImg'

// types
import type { FileUploaded } from '../../../../services/web/useTempFileService'
import type { Messenger, MessengerRequest, SenderRole } from '../../../../services/web/useMessenger'
import type { FileInfo } from '../../../../services/web/types/responseWebServiceTypes'

interface MessengerInputProps {
    projectId: number
    onMessageSent?: (messenger: Messenger) => void
}

const MessengerInput = (props: MessengerInputProps) => {
    const { projectId, onMessageSent } = props

    const translate = useTranslate()
    const classes = useStyles()
    const checkPermission = useCheckPermission()
    const senderRole: SenderRole = checkPermission.isUserResearcher() ? 'researcher' : 'officer'
    const alertMessageAction = useAlertMessageAction()
    const messengerServices = useMessenger()
    const tempFileServices = useTempFileService()

    const fileInputRef = useRef<any>()
    const [messageText, setMessageText] = useState<string>('')
    const [fileInfo, setFileInfo] = useState<FileInfo | null>(null)
    const [isSending, setIsSending] = useState<boolean>(false)

    const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            sendMessage()
        }
    }

    const isValidExtension = (fileName: string): boolean => {
        const allowed = accept.split(',').map((ext) => ext.trim().replace('.', '').toLowerCase())

        const parts = fileName.split('.')
        if (parts.length < 2) return false

        const ext = parts.pop()?.toLowerCase()
        return !!ext && allowed.includes(ext)
    }

    const isValid = (file: File): boolean => {
        if (!isValidExtension(file.name)) {
            alertMessageAction.openAlertMessage({
                title: translate.get('ALERT.TITLE.WARNING'),
                message: translate.get('ALERT.CONTENT.UPLOAD_INVALID_FILE_EXTENSION'),
            })
            return false
        }

        if (file.size > MAX_FILE_SIZE) {
            alertMessageAction.openAlertMessage({
                title: translate.get('ALERT.TITLE.WARNING'),
                message: translate.get('LABEL.UPLOAD_NOT_EXCEED_30_MB'),
            })
            return false
        }

        return true
    }

    const uploadTempFile = async (file: File): Promise<FileUploaded | null> => {
        try {
            const formData = new FormData()
            formData.append('files', file)
            const { result } = await tempFileServices.uploadTempFile(formData)
            return result[0]
        } catch (error) {
            console.error(error)
            return null
        }
    }

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files

        if (!files || files.length === 0) {
            return false
        }

        const file = files[0]

        if (!isValid(file)) {
            return false
        }

        const tempFile = await uploadTempFile(file)

        if (!tempFile) {
            return
        }

        setFileInfo({ ...tempFile, date: new Date().toISOString() })

        if (fileInputRef.current) {
            fileInputRef.current.value = ''
        }
    }

    const handleAttachFileClick = () => {
        fileInputRef.current?.click()
    }

    const sendMessage = async () => {
        try {
            if (isSending) return
            setIsSending(true)

            const messengerReqeust: MessengerRequest = {
                senderRole,
                messageText,
                documentInfo: fileInfo ? [{ fileTokenInfo: [fileInfo.fileToken] }] : null,
            }
            const { result } = await messengerServices.createMessenger(projectId, messengerReqeust)

            onMessageSent && onMessageSent(result)
            setFileInfo(null)
            setMessageText('')
        } catch (error) {
            console.error(error)
        } finally {
            setIsSending(false)
        }
    }

    return (
        <>
            {fileInfo ? (
                <Box className={classes.documentInfo}>
                    <AttachFile />
                    <img src={process.env.PUBLIC_URL + getFileIconPath(fileInfo.filename)} alt="file icon" />
                    <span>{fileInfo.filename}</span>
                    <IconButton size="small" color="secondary" onClick={() => setFileInfo(null)}>
                        <HighlightOff />
                    </IconButton>
                </Box>
            ) : null}

            <Paper elevation={0} className={classes.inputWrapper}>
                <InputBase
                    className={classes.input}
                    placeholder={translate.get('UM.MESSAGE.TYPE_MESSAGE')}
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    onKeyDown={handleKeyDown}
                    multiline
                />
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".png,.jpeg,.tiff,.pdf,.doc,.docx"
                    hidden
                    onChange={handleFileChange}
                />
                <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<AttachFile />}
                    disabled={!!fileInfo}
                    onClick={handleAttachFileClick}
                >
                    {translate.get('UM.MESSAGE.ATTACH_FILE')}
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<Send />}
                    disabled={(!messageText && !fileInfo) || isSending}
                    onClick={sendMessage}
                >
                    {translate.get('UM.MESSAGE.SEND')}
                </Button>
            </Paper>
        </>
    )
}

const useStyles = makeStyles((theme) => ({
    inputWrapper: {
        marginInline: 32,
        display: 'flex',
        alignItems: 'flex-end',
        gap: 8,
        padding: theme.spacing(1),
        borderRadius: 24,
        border: `1px solid ${theme.palette.grey[300]}`,
        marginTop: 'auto',
    },

    input: {
        flex: 1,
        fontSize: '1rem',
        lineHeight: 1.5,
        padding: theme.spacing(1, 2),
        borderRadius: 24,
        resize: 'none',
    },

    documentInfo: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: 12,
        color: theme.palette.common.black,
        backgroundColor: theme.palette.common.white,
        border: '1px solid rgba(220, 220, 220, 1)',
        borderRadius: 4,
        marginInline: 32,
        marginBottom: 8,
        alignSelf: 'flex-start',

        '& img': {
            flexShrink: 0,
        },

        '& span': {
            flexGrow: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
        },
    },
}))

export default MessengerInput
